import { createFileRoute, useNavigate } from '@tanstack/react-router';
import React from 'react';
import z from 'zod';
import {
  BATTALION_COLUMNS,
  BattalionHero,
  BattalionRow,
  BattalionSaveBar,
  BattalionSection,
  BulkDossierBar,
  CommandSelect,
  DossierPreview,
  LinkRecruitsDialog,
  MemberGroupHeader,
  MemberRow,
  memberGroups,
  MiniBar,
  pl,
  SquareCheck,
  StatusSwitchField,
  TaskComposer,
  TaskPhaseFilter,
  TaskRow,
} from '@/components/ui/admin/bataillons';
import { Field, SearchField } from '@/components/ui/admin/field';
import { PrimaryPill, TopbarActions, TopbarPill } from '@/components/ui/admin/gallery';
import { RichTextEditor } from '@/components/ui/admin/rich-text-editor';
import { Content } from '@/components/ui/content';
import { Icon } from '@/components/ui/icon';
import { userRoleOptions } from '@/db/schema/users';
import { useDebounce } from '@/hooks/use-debounce';
import { meta } from '@/lib/meta';
import {
  type BataillonDetail,
  type BataillonForm,
  type CommandPerson,
  fetchDossier,
  TASK_PHASE_ORDER,
  type TaskPhase,
  useBataillonDetail,
  useBataillonOverview,
  useCommandOptions,
  useLinkCandidates,
  useLinkSoldiers,
  useSaveBataillon,
  useTaskMutations,
  useUnlinkSoldier,
} from '@/services/bataillons';

/**
 * Batalhões (prints 01–06 da skill `export-batalhoes`). Substitui a tela dinâmica `$resource` de bataillons (rota estática tem prioridade).
 * Igual ao check-in, a ficha aberta fica na URL (`?batalhao=<id>` · `?batalhao=novo`) e não num segmento,
 * para não virar permissão de `update` no AppLayout.
 */
export const Route = createFileRoute('/_app/bataillons')({
  head: () => meta({ label: 'Batalhões' }),
  validateSearch: z.object({ batalhao: z.string().optional() }),
  component: RouteComponent,
});

function RouteComponent() {
  const { batalhao } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const open = (id?: string) => navigate({ search: id ? { batalhao: id } : {} });
  const [dossier, setDossier] = React.useState<BataillonDetail[] | null>(null);
  const [loadingDossier, setLoadingDossier] = React.useState(false);

  const emit = async (ids: string[]) => {
    setLoadingDossier(true);
    try {
      setDossier(await fetchDossier(ids));
    } finally {
      setLoadingDossier(false);
    }
  };

  return (
    <>
      {batalhao ? (
        <DetailStep
          id={batalhao}
          onBack={() => open()}
          onEmit={(id) => emit([id])}
          onCreated={(id) => navigate({ search: { batalhao: id }, replace: true })}
        />
      ) : (
        <ListStep onOpen={open} onEmit={emit} emitting={loadingDossier} />
      )}
      {dossier && (
        <DossierPreview
          sheets={dossier}
          issuedAt={new Date().toLocaleDateString('pt-BR')}
          onClose={() => setDossier(null)}
        />
      )}
    </>
  );
}

/* ───────────── Listagem ───────────── */

function ListStep({
  onOpen,
  onEmit,
  emitting,
}: {
  onOpen: (id: string) => void;
  onEmit: (ids: string[]) => void;
  emitting: boolean;
}) {
  const { data = [], isLoading } = useBataillonOverview();
  const [q, setQ] = React.useState('');
  const [sel, setSel] = React.useState<string[]>([]);
  const ql = useDebounce(q.trim().toLowerCase(), 250);
  const rows = data.filter(
    (b) =>
      !ql ||
      [
        b.name,
        b.major?.name,
        b.kidsMajor?.name,
        b.kidsCaptain?.name,
        b.youthCaptain?.name,
      ]
        .join(' ')
        .toLowerCase()
        .includes(ql),
  );
  const allOn = rows.length > 0 && rows.every((r) => sel.includes(r.id));
  const toggle = (id: string) =>
    setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  return (
    <Content
      isLoading={isLoading}
      breadcrumbs={{
        title: 'Batalhões',
        items: [{ label: 'Comando' }, { label: 'Listagem', active: true }],
        action: <PrimaryPill onClick={() => onOpen('novo')}>Novo batalhão</PrimaryPill>,
      }}
    >
      <div className="flex flex-col gap-5 pb-24">
        {/* O SearchField dimensiona por flex-basis (pensado p/ linha): sem este wrapper vira altura. */}
        <div className="flex">
          <SearchField
            icon={<Icon name="search" className="size-[18px]" />}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por batalhão, major ou capitão"
          />
        </div>
        <div className="overflow-hidden rounded-2xl border border-border bg-card">
          <div className="flex items-center gap-3 border-b border-border px-[22px] py-4">
            <span className="col-label text-[10.5px] text-muted-foreground">{`Exibindo ${rows.length} de ${data.length} batalhões`}</span>
            <span className="ml-auto text-[11.5px] font-semibold text-faint">
              Selecione batalhões para emitir o dossiê
            </span>
          </div>
          <div className="overflow-x-auto">
            <div className="min-w-[1040px]">
              <div
                className="grid items-center border-b border-border px-[22px]"
                style={{ gridTemplateColumns: BATTALION_COLUMNS }}
              >
                <SquareCheck
                  checked={allOn}
                  onChange={() => setSel(allOn ? [] : rows.map((r) => r.id))}
                  label="Selecionar todos"
                />
                {[
                  'Batalhão',
                  'Major Kids',
                  'Capitão Kids',
                  'Major Jovens',
                  'Capitão Jovens',
                ].map((h) => (
                  <span key={h} className="col-label py-[13px] text-faint">
                    {h}
                  </span>
                ))}
                <span className="col-label py-[13px] text-right text-faint">
                  Soldados
                </span>
                <span className="col-label py-[13px] pl-6 text-faint">Missões</span>
                <span className="col-label py-[13px] text-right text-faint">Ações</span>
              </div>
              {isLoading
                ? Array.from({ length: 6 }, (_, i) => (
                    <div
                      key={i}
                      className="h-[69px] animate-pulse border-b border-border bg-raised/40"
                    />
                  ))
                : rows.map((b) => (
                    <BattalionRow
                      key={b.id}
                      b={b}
                      selected={sel.includes(b.id)}
                      onToggle={() => toggle(b.id)}
                      onOpen={() => onOpen(b.id)}
                      onPrint={() => onEmit([b.id])}
                    />
                  ))}
              {!isLoading && !rows.length && (
                <p className="px-6 py-12 text-center text-[13px] text-muted-foreground">
                  Nenhum batalhão encontrado.
                </p>
              )}
            </div>
          </div>
        </div>
        {sel.length > 0 && (
          <BulkDossierBar
            count={sel.length}
            loading={emitting}
            onClear={() => setSel([])}
            onEmit={() => onEmit(data.filter((b) => sel.includes(b.id)).map((b) => b.id))}
          />
        )}
      </div>
    </Content>
  );
}

/* ───────────── Ficha ───────────── */

const EMPTY: BataillonForm = {
  name: '',
  tag: null,
  isActive: true,
  majorId: null,
  kidsMajorId: null,
  kidsCaptainId: null,
  youthCaptainId: null,
  description: null,
};

function DetailStep({
  id,
  onBack,
  onEmit,
  onCreated,
}: {
  id: string;
  onBack: () => void;
  onEmit: (id: string) => void;
  /** Batalhão novo salvo: troca `?batalhao=novo` pelo id (sem empilhar histórico). */
  onCreated: (id: string) => void;
}) {
  const isNew = id === 'novo';
  const { data } = useBataillonDetail(isNew ? undefined : id);
  const save = useSaveBataillon();
  const tasks = useTaskMutations(id);
  const link = useLinkSoldiers(id);
  const unlink = useUnlinkSoldier(id);

  // Formulário local (seções 01 + 02). Missões e efetivo salvam na hora (otimista), não pela SaveBar.
  const [form, setForm] = React.useState<BataillonForm>(EMPTY);
  const [people, setPeople] = React.useState<
    Record<'major' | 'kidsMajor' | 'kids' | 'youth', CommandPerson | null>
  >({ major: null, kidsMajor: null, kids: null, youth: null });
  const [dirty, setDirty] = React.useState(isNew);
  React.useEffect(() => {
    if (!data) return;
    setForm({
      name: data.name,
      tag: data.tag,
      isActive: data.isActive,
      majorId: data.majorId,
      kidsMajorId: data.kidsMajorId,
      kidsCaptainId: data.kidsCaptainId,
      youthCaptainId: data.youthCaptainId,
      description: data.description,
    });
    setPeople({
      major: data.major,
      kidsMajor: data.kidsMajor,
      kids: data.kidsCaptain,
      youth: data.youthCaptain,
    });
    setDirty(false);
  }, [data]);
  const set = (p: Partial<BataillonForm>) => {
    setForm((f) => ({ ...f, ...p }));
    setDirty(true);
  };

  // Todo o comando vem de Usuários: Major (Kids e Jovens) = perfil major, Capitão (Kids e Jovens) = perfil capitão.
  const [cmdQ, setCmdQ] = React.useState<Record<string, string>>({});
  const majors = useCommandOptions('major');
  const captains = useCommandOptions('capitao');
  const optsFor = (list: typeof majors.data, key: string) =>
    (list ?? [])
      .filter(
        (m) => !cmdQ[key] || m.name.toLowerCase().includes(cmdQ[key]!.toLowerCase()),
      )
      .map((m) => ({
        ...m,
        meta: userRoleOptions.find((o) => o.value === m.role)?.label,
      }));

  const [linkOpen, setLinkOpen] = React.useState(false);
  const [lq, setLq] = React.useState('');
  const [scope, setScope] = React.useState<'free' | 'all'>('free');
  const candidates = useLinkCandidates(
    id,
    { search: useDebounce(lq.trim(), 300), scope },
    linkOpen && !isNew,
  );
  const freeCount =
    useLinkCandidates(id, { scope: 'free' }, linkOpen && !isNew).data?.length ?? 0;
  const allCount =
    useLinkCandidates(id, { scope: 'all' }, linkOpen && !isNew).data?.length ?? 0;

  const b: BataillonDetail =
    data ??
    ({
      id,
      ...EMPTY,
      major: null,
      kidsMajor: null,
      kidsCaptain: null,
      youthCaptain: null,
      tasks: [],
      soldiers: [],
    } as any);
  const done = b.tasks.filter((t) => t.done).length;
  const [phaseFilter, setPhaseFilter] = React.useState<TaskPhase | 'all'>('all');
  const phaseCounts = {
    all: b.tasks.length,
    before: b.tasks.filter((t) => t.phase === 'before').length,
    during: b.tasks.filter((t) => t.phase === 'during').length,
    after: b.tasks.filter((t) => t.phase === 'after').length,
  };
  const commandFilled = [
    form.kidsMajorId,
    form.kidsCaptainId,
    form.majorId,
    form.youthCaptainId,
  ].filter(Boolean).length;

  const onSave = () =>
    save.mutate(
      { id: isNew ? undefined : id, data: form },
      {
        onSuccess: (res: any) => {
          setDirty(false);
          if (isNew && res?.id) onCreated(res.id);
        },
      },
    );

  return (
    <Content
      breadcrumbs={{
        title: form.name || 'Novo batalhão',
        items: [
          { label: 'Comando' },
          { label: 'Batalhões', onClick: onBack },
          { label: 'Ficha', active: true },
        ],
        onBack,
        action: isNew ? undefined : (
          <TopbarActions>
            <TopbarPill icon="file-text" onClick={() => onEmit(id)}>
              Emitir dossiê
            </TopbarPill>
          </TopbarActions>
        ),
      }}
    >
      <div className="flex max-w-[1440px] flex-col gap-5 pb-24">
        <BattalionHero
          b={{ ...b, ...form }}
          soldiers={b.soldiers.length}
          tasksLabel={`${done}/${b.tasks.length}`}
          commandFilled={commandFilled}
        />

        <div className="grid items-start gap-5 [grid-template-columns:repeat(auto-fit,minmax(min(100%,520px),1fr))]">
          <div className="flex min-w-0 flex-col gap-5">
            <BattalionSection
              index="01"
              title="Identificação e comando"
              description="Todo o comando vem de Usuários: majors com perfil Major e capitães com perfil Capitão."
            >
              <div className="grid grid-cols-[minmax(0,1fr)_120px_auto] gap-4">
                <Field
                  label="Nome"
                  required
                  value={form.name}
                  onChange={(e) => set({ name: e.target.value })}
                  placeholder="Ex: Batalhão da Logística"
                />
                <Field
                  label="Sigla"
                  value={form.tag ?? ''}
                  maxLength={4}
                  onChange={(e) => set({ tag: e.target.value.toUpperCase() || null })}
                  placeholder="LOG"
                />
                <StatusSwitchField
                  value={form.isActive}
                  onChange={(v) => set({ isActive: v })}
                />
              </div>
              <CommandSelect
                label="Major Kids"
                source="Usuários"
                icon="shield"
                value={people.kidsMajor}
                options={optsFor(majors.data, 'kidsMajor')}
                search={cmdQ.kidsMajor ?? ''}
                onSearch={(v) => setCmdQ((q) => ({ ...q, kidsMajor: v }))}
                loading={majors.isLoading}
                onPick={(p) => {
                  setPeople((s) => ({ ...s, kidsMajor: p }));
                  set({ kidsMajorId: p?.id ?? null });
                }}
              />
              <CommandSelect
                label="Capitão Kids"
                source="Usuários"
                icon="user"
                value={people.kids}
                options={optsFor(captains.data, 'kids')}
                search={cmdQ.kids ?? ''}
                onSearch={(v) => setCmdQ((q) => ({ ...q, kids: v }))}
                loading={captains.isLoading}
                onPick={(p) => {
                  setPeople((s) => ({ ...s, kids: p }));
                  set({ kidsCaptainId: p?.id ?? null });
                }}
              />
              <CommandSelect
                label="Major Jovens"
                source="Usuários"
                icon="shield"
                value={people.major}
                options={optsFor(majors.data, 'major')}
                search={cmdQ.major ?? ''}
                onSearch={(v) => setCmdQ((q) => ({ ...q, major: v }))}
                loading={majors.isLoading}
                onPick={(p) => {
                  setPeople((s) => ({ ...s, major: p }));
                  set({ majorId: p?.id ?? null });
                }}
              />
              <CommandSelect
                label="Capitão Jovens"
                source="Usuários"
                icon="user"
                value={people.youth}
                options={optsFor(captains.data, 'youth')}
                search={cmdQ.youth ?? ''}
                onSearch={(v) => setCmdQ((q) => ({ ...q, youth: v }))}
                loading={captains.isLoading}
                onPick={(p) => {
                  setPeople((s) => ({ ...s, youth: p }));
                  set({ youthCaptainId: p?.id ?? null });
                }}
              />
            </BattalionSection>

            <BattalionSection
              index="02"
              title="Descrição do batalhão"
              description="Propósito, funções e orientações. Aparece no app e no dossiê."
              flush
            >
              <RichTextEditor
                value={form.description}
                onChange={(html) => set({ description: html })}
              />
            </BattalionSection>

            <BattalionSection
              index="03"
              title="Missões do batalhão"
              description={
                isNew
                  ? 'Salve o batalhão para adicionar missões.'
                  : 'Tarefas que o batalhão executa durante a operação.'
              }
              trailing={
                <span className="flex flex-col items-end gap-[7px]">
                  <span className="font-display text-[15px] font-extrabold text-white">{`${done}/${b.tasks.length}`}</span>
                  <MiniBar
                    value={b.tasks.length ? done / b.tasks.length : 0}
                    className="w-[90px]"
                  />
                </span>
              }
              flush
            >
              {!isNew && (
                <TaskComposer
                  onAdd={(title, phase) => tasks.add.mutate({ title, phase })}
                />
              )}
              {!isNew && b.tasks.length > 0 && (
                <TaskPhaseFilter
                  value={phaseFilter}
                  onChange={setPhaseFilter}
                  counts={phaseCounts}
                />
              )}
              {b.tasks.map((t, i) =>
                phaseFilter !== 'all' && t.phase !== phaseFilter ? null : (
                  <TaskRow
                    key={t.id}
                    index={i}
                    task={t}
                    onToggle={() => tasks.toggle.mutate(t)}
                    onRemove={() => tasks.remove.mutate(t)}
                    onNextPhase={() =>
                      tasks.setPhase.mutate({
                        task: t,
                        phase:
                          TASK_PHASE_ORDER[
                            (TASK_PHASE_ORDER.indexOf(t.phase) + 1) %
                              TASK_PHASE_ORDER.length
                          ]!,
                      })
                    }
                  />
                ),
              )}
              {!isNew && b.tasks.length > 0 && !phaseCounts[phaseFilter] && (
                <p className="m-[18px_26px] rounded-xl border border-dashed border-border p-[18px] text-center text-[13px] text-muted-foreground">
                  Nenhuma missão nesta fase.
                </p>
              )}
              {!isNew && !b.tasks.length && (
                <p className="m-[18px_26px] rounded-xl border border-dashed border-border p-[18px] text-center text-[13px] text-muted-foreground">
                  Nenhuma missão cadastrada ainda.
                </p>
              )}
            </BattalionSection>
          </div>

          <div className="sticky top-24 min-w-0 overflow-hidden rounded-2xl border border-border bg-card">
            <div className="flex items-start gap-4 border-b border-border px-6 py-[22px]">
              <span className="font-display pt-[3px] text-[13px] font-extrabold text-faint">
                04
              </span>
              <span className="flex min-w-0 flex-1 flex-col gap-1.5">
                <span className="font-display text-[18px] font-extrabold uppercase tracking-[-.02em] text-white">
                  Efetivo
                </span>
                <span className="text-[13px] text-muted-foreground">
                  {pl(b.soldiers.length, 'soldado vinculado', 'soldados vinculados')} a
                  este batalhão.
                </span>
              </span>
              <button
                type="button"
                disabled={isNew}
                onClick={() => {
                  setLq('');
                  setScope('free');
                  setLinkOpen(true);
                }}
                className="flex h-10 shrink-0 items-center gap-2 rounded-full bg-sand px-4 text-[13px] font-bold text-on-sand hover:bg-sand/90 disabled:opacity-40"
              >
                <Icon name="user-plus" className="size-4" />
                Vincular recruta
              </button>
            </div>
            <div className="max-h-[640px] overflow-y-auto">
              {memberGroups(b.soldiers).map((g) => (
                <React.Fragment key={g.key}>
                  <MemberGroupHeader label={g.label} count={g.items.length} />
                  {g.items.map((m) => (
                    <MemberRow
                      key={m.id}
                      m={m}
                      captain={
                        m.userId && form.kidsCaptainId === m.userId
                          ? 'Capitão Kids'
                          : m.userId && form.youthCaptainId === m.userId
                            ? 'Capitão Jovens'
                            : undefined
                      }
                      onUnlink={() => unlink.mutate(m)}
                    />
                  ))}
                </React.Fragment>
              ))}
              {!b.soldiers.length && (
                <div className="flex flex-col items-center gap-2.5 px-6 py-9 text-center">
                  <span className="flex size-12 items-center justify-center rounded-full border border-dashed border-ring text-muted-foreground">
                    <Icon name="users" className="size-5" />
                  </span>
                  <span className="font-display text-[15px] font-extrabold uppercase text-white">
                    Sem soldados
                  </span>
                  <span className="text-[13px] text-muted-foreground">
                    Vincule recrutas para formar o efetivo.
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <BattalionSaveBar
        status={dirty ? 'dirty' : save.isSuccess ? 'saved' : 'clean'}
        onBack={onBack}
        onSave={onSave}
        saving={save.isPending}
      />

      <LinkRecruitsDialog
        open={linkOpen}
        battalionName={form.name}
        candidates={candidates.data ?? []}
        counts={{ free: freeCount, all: allCount }}
        scope={scope}
        onScope={setScope}
        search={lq}
        onSearch={setLq}
        saving={link.isPending}
        onClose={() => setLinkOpen(false)}
        onConfirm={(ids) => link.mutate(ids, { onSuccess: () => setLinkOpen(false) })}
      />
    </Content>
  );
}
